/**
 * Read-only rendering of one programmed workout slot.
 *
 * Shared by the Team Program Manager and the Flexible Program Manager. The two
 * differ in how they iterate slots - teams lay them out across the seven
 * weekdays, a flexible program lists them as D1..Dn ordinals - but the contents
 * of a slot card (section separators, exercise lines, set targets) are
 * identical, so they live here rather than in either manager.
 */
import isEqual from "lodash/isEqual";
import _round from "lodash/round";
import { Fragment, useEffect, useState } from "react";
import ContentLoader from "react-content-loader";
import { OUTCOME_STATUS } from "../../../constants";
import MetconDisplay from "../../../parts/metcon/MetconDisplay";
import { getExerciseName } from "../../../parts/modals/learn-more/blockHelpers";
import { formattedTarget, getSetCounts } from "../../../utils/actuals";
import {
  computeSectionPrefixes,
  groupExercisesBySection,
} from "../../../utils/prefix-calculator";
import SectionSeparator from "../../athlete-portal/calendar/components/EditMode/ExerciseEditor/SectionSeparator";

const { COMPLETED, MISSED } = OUTCOME_STATUS;

/**
 * Logged attempts for a set, collapsed into runs of identical results -
 * `set.outcomes` is keyed by attempt index ("0", "1", ...) since a set's
 * `sets` count can be logged one at a time, and those attempts don't have to
 * agree (e.g. an ascending pyramid: 5@55, then 5@67). Flattening them into a
 * single line by only reading the last one would misreport every earlier,
 * different attempt as if it matched it - three genuinely identical sets
 * still read as one "3 x 5 @ 135lb" line, but two different ones become two
 * lines. Returns [] when nothing has been logged yet, so the caller falls
 * back to the prescribed line unchanged.
 */
const outcomeGroups = (set) => {
  const keys = Object.keys(set.outcomes || {}).sort(
    (a, b) => Number(a) - Number(b)
  );
  const groups = [];
  for (const key of keys) {
    const entry = set.outcomes[key];
    if (!entry?.outcome) continue;
    const last = groups[groups.length - 1];
    if (last && isEqual(last.values, entry.outcome)) {
      last.count += 1;
      last.statuses.push(entry.status);
    } else {
      groups.push({
        values: entry.outcome,
        count: 1,
        statuses: [entry.status],
      });
    }
  }
  return groups;
};

/** One exercise line: name, prefix, and a dot-per-set target summary. */
export const SessionExercise = ({
  exercise,
  sectionPrefix,
  fromMetric,
  withWeight,
  intensityMeasure,
  isRiR,
  units,
}) => {
  const prefix = (sectionPrefix || "").replace(")", "");

  const precision = units === "metric" ? 2 : 1;
  const isMetcon = exercise.primary === "metcon";

  return (
    <div key={`${exercise._id}`} className="exercise-frame">
      <div className="flex items-center gap-2">
        <span className="calendar-prefix">{prefix}</span>
        <p>{getExerciseName(exercise)}</p>
      </div>
      <div className="body">
        {isMetcon && <MetconDisplay exercise={exercise} compact />}
        {exercise.sets.map((elem, idx) => {
          const isPowerlifting =
            exercise.type === "primary" || exercise.type === "secondary";
          const groups = outcomeGroups(elem);

          if (!groups.length) {
            // Nothing logged yet - the original single prescribed line.
            const { total, completed, missed } = getSetCounts(exercise, elem);
            const load = elem.load
              ? _round(fromMetric(elem.load, units), precision)
              : 0;
            const targetLoad = !!load && withWeight(load, units);
            return (
              <div
                className="exercise-element-set-wrapper"
                key={`${exercise._id}${idx}`}
              >
                <div className="exercise-element-set expanded">
                  <span
                    className={
                      completed + missed === total
                        ? "dot bg-(--success)"
                        : missed === 0 && completed === 0
                          ? "dot bg-(--white-20)"
                          : "dot bg-(--information)"
                    }
                  ></span>
                  {formattedTarget(
                    elem,
                    isPowerlifting,
                    elem.load ? `${_round(elem.percentRepMax, 1)}` : "",
                    "metric",
                    targetLoad,
                    intensityMeasure,
                    isRiR
                  )}
                </div>
              </div>
            );
          }

          // One line per distinct logged result, each with its own dot -
          // sets is overridden to that group's own count, not the
          // prescription's, so "2 x 5 @ 67lb" only appears when both really
          // were 67lb.
          return groups.map((group, groupIdx) => {
            const completed = group.statuses.filter(
              (status) => status === COMPLETED
            ).length;
            const missed = group.statuses.filter(
              (status) => status === MISSED
            ).length;
            // `sets: group.count` has to come LAST: the outcome itself
            // carries its own `sets` field (a copy of the prescription, not
            // "how many of these are in this group"), and spreading
            // group.values after it would silently clobber the real count
            // back to the prescribed one.
            const displayValues = {
              ...elem,
              ...group.values,
              sets: group.count,
            };
            const load = displayValues.load
              ? _round(fromMetric(displayValues.load, units), precision)
              : 0;
            const targetLoad = !!load && withWeight(load, units);
            return (
              <div
                className="exercise-element-set-wrapper"
                key={`${exercise._id}${idx}-${groupIdx}`}
              >
                <div className="exercise-element-set expanded">
                  <span
                    className={
                      completed + missed === group.count
                        ? "dot bg-(--success)"
                        : missed === 0 && completed === 0
                          ? "dot bg-(--white-20)"
                          : "dot bg-(--information)"
                    }
                  ></span>
                  <span className="text-(--success)">
                    {formattedTarget(
                      displayValues,
                      isPowerlifting,
                      // %1RM has no equivalent on a logged outcome (that
                      // needs the athlete's rep max, which isn't loaded
                      // here).
                      "",
                      "metric",
                      targetLoad,
                      intensityMeasure,
                      isRiR
                    )}
                  </span>
                </div>
              </div>
            );
          });
        })}
      </div>
    </div>
  );
};

/**
 * The body of a slot card: section separators interleaved with their exercises,
 * then any sections that hold no exercises.
 *
 * Prefixes restart at "A" inside each section, matching the calendar.
 */
export const SlotExerciseList = ({
  session,
  sessionSections,
  keyPrefix,
  fromMetric,
  withWeight,
  intensityMeasure,
  isRiR,
  units,
}) => {
  const exercises = session || [];
  const sections = sessionSections || [];

  const sectionPrefixes = computeSectionPrefixes(exercises, sections);

  const sectionByFirstExerciseId = {};
  const emptySections = [];
  groupExercisesBySection(exercises, sections).forEach((group) => {
    if (!group.section) return;
    if (group.exercises.length) {
      sectionByFirstExerciseId[group.exercises[0]._id] = group.section;
    } else {
      emptySections.push(group.section);
    }
  });

  return (
    <>
      {exercises.map((exercise) => (
        <Fragment key={`${exercise._id}-${keyPrefix}`}>
          {sectionByFirstExerciseId[exercise._id] && (
            <SectionSeparator
              section={sectionByFirstExerciseId[exercise._id]}
              editable={false}
              nameEditable={false}
            />
          )}
          <SessionExercise
            exercise={exercise}
            sectionPrefix={sectionPrefixes[exercise._id]}
            withWeight={withWeight}
            fromMetric={fromMetric}
            intensityMeasure={intensityMeasure}
            isRiR={isRiR}
            units={units}
          />
        </Fragment>
      ))}
      {emptySections.map((section) => (
        <SectionSeparator
          key={section._id}
          section={section}
          editable={false}
          nameEditable={false}
        />
      ))}
    </>
  );
};

/** Seven placeholder cards while a week's exercises are being fetched. */
export const CardsSkeleton = ({ backgroundColor, foregroundColor }) => {
  const [scale, setScale] = useState(window.innerWidth / 1728);

  useEffect(() => {
    const handleResize = () => setScale(window.innerWidth / 1728);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Base configuration for width 1500 baseline
  const cardWidth = 190;
  const cardHeight = 400;
  const gap = 15;
  const startX = 20;
  const y = 20;
  const rx = 8;
  const ry = 8;

  const baseRects = Array.from(
    { length: 7 },
    (_, i) => startX + i * (cardWidth + gap)
  );

  return (
    <ContentLoader
      viewBox={`0 0 ${1500 * scale} ${450 * scale}`}
      width={1500 * scale}
      height={450 * scale}
      backgroundColor={backgroundColor}
      foregroundColor={foregroundColor}
      style={{ width: "100%", height: "auto" }}
    >
      {baseRects.map((x) => (
        <rect
          key={`skeleton-card-${x}`}
          x={x * scale}
          y={y * scale}
          rx={rx * scale}
          ry={ry * scale}
          width={cardWidth * scale}
          height={cardHeight * scale}
        />
      ))}
    </ContentLoader>
  );
};
